<template>
	<button
		@click="openDialog"
		class="link">
		View Details	
	</button>
	<tw-modal
		:title="`Invoice #${ props.invoice.invoiceNumber }`"
		modal-max-width="max-w-screen-sm"
		:initial-focus="initialFocusTarget"
		ref="invoiceDetailsModal">
		<dl
			ref="initialFocusTarget"
			tabindex="-1"
			class="mt-6 grid grid-cols-1 text-sm leading-6 sm:grid-cols-2">
			<div class="flex items-start justify-between sm:col-span-2">
				<div>
					<tw-simple-stat
						label="Invoice Total">
						{{ currencyFormatter.format(invoiceTotalWithoutProcessingFee(props.invoice)) }}
					</tw-simple-stat>
				</div>
				<div
					v-if="showStatusBadge"
					class="text-right">
					<tw-badge-flat
						:color="invoiceStatusColor">
						{{ invoiceStatusLabel }}
					</tw-badge-flat>
					<div class="mt-2">
						<dt class="inline text-gray-500">
							Payment Terms
						</dt>
						{{ ' ' }}
						<dd class="inline text-gray-700 font-bold">
							{{ props.invoice.paymentTerms }}
						</dd>
					</div>
				</div>
			</div>

			<div class="sm:pr-4 mt-6 border-t border-gray-900/5 pt-6">
				<dt class="inline text-gray-500">
					Invoiced on
				</dt>
				{{ ' ' }}
				<dd class="inline text-gray-700 font-bold">
					<time>{{ props.invoice.invoiceDate }}</time>
				</dd>
			</div>

			<div class="sm:pl-4 sm:mt-6 sm:border-t sm:border-gray-900/5 sm:pt-6">
				<dt class="inline text-gray-500">
					Due on
				</dt>
				{{ ' ' }}
				<dd
					v-if="!isDueOnReceipt"
					class="inline text-gray-700 font-bold">
					<time>{{ props.invoice.dueDate }}</time>
				</dd>
				<dd
					v-else
					class="inline text-gray-700 font-bold">
					<time>Receipt</time>
				</dd>
			</div>

		
			<div class="mt-6 sm:border-t sm:border-gray-900/5 sm:pt-6 sm:col-span-2">
				<dt class="text-sm font-medium text-gray-500">
					Summary
				</dt>
				<dd>
					<p
						v-for="(item, idx) in props.invoice.summaries"
						:key="idx">
						{{ item }}
					</p>
				</dd>
			</div>
			<div class="mt-6 sm:border-t sm:border-gray-900/5 sm:pt-6 sm:col-span-2">
				<dt class="text-sm font-medium text-gray-500">
					Services
				</dt>
				<dd>
					<div
						v-for="(service, idx) in servicesWithoutProcessingFees"
						:key="idx">
						{{ service.description }}
					</div>
				</dd>
			</div>
		</dl>
		<div class="mt-8 flex flex-row items-center justify-end gap-3 *:w-full sm:flex-row sm:*:w-auto">
			<tw-button
				class="btn-base btn-indigo w-full"
				:loading="downloadLoading"
				:disabled="downloadLoading"
				@click="downloadInvoice()">
				Download Invoice
				<template #icon_right>
					<ArrowDownTrayIcon class="w-5 h-5" />
				</template>
			</tw-button>
			<a
				v-if="props.invoice.status === 'Paid' && props.invoice.receipt"
				:href="props.invoice.receipt"
				target="_blank">
				<tw-button class="btn-base btn-indigo w-full">
					View Receipt
					<template #icon_right>
						<ArrowUpRightIcon class="w-5 h-5" />
					</template>
				</tw-button>
			</a>
		</div>
	</tw-modal>
</template>

<script lang="ts">
	export default { name: 'InvoiceDetailsButton' } // name the component
</script>

<script setup lang="ts">
	import api from '@/utilities/api'

	import {
		ArrowDownTrayIcon,
		ArrowUpRightIcon,
	} from "@heroicons/vue/20/solid"

	import {
		Invoice
	} from '@/types'

	import {
		isDueOnReceipt as isInvoiceDueOnReceipt,
		showStatusBadge as isStatusBadgeVisible,
		invoiceStatusColor as invoiceBadgeColor,
		invoiceStatusLabel as invoiceBadgeLabel
	} from '@/utilities/invoiceStatus'

	import {		
		Auth
	} from 'aws-amplify'

	import {
		computed,
		ref,
		PropType
	} from 'vue'

	const props = defineProps({
		invoice: {
			type: Object as PropType<Invoice>,
			default: () => {return {} as Invoice}
		},
	})

	const invoiceDetailsModal = ref<{open: boolean}>({open: false})
	const initialFocusTarget = ref<HTMLElement | null>(null)

	const currencyFormatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	})

	const openDialog = () => {
		invoiceDetailsModal.value.open = true
	}
	
	const invoiceStatusColor = computed(() => invoiceBadgeColor(props.invoice))

	const invoiceStatusLabel = computed(() => invoiceBadgeLabel(props.invoice))

	const isDueOnReceipt = computed(() => isInvoiceDueOnReceipt(props.invoice.paymentTerms))

	const showStatusBadge = computed(() => isStatusBadgeVisible(props.invoice))

	const servicesWithoutProcessingFees = computed(() => {
		return props.invoice.services.filter((s: {description: string, total: number}) => {
			return !s.description.toLowerCase().includes('processing fee')
		})
	})

	const downloadLoading = ref(false)
	async function downloadInvoice() {
		try {
			downloadLoading.value = true
			const user = await Auth.currentAuthenticatedUser()

			const body = {
				action: "downloadInvoice",
				payload: {
					id: props.invoice.id,
					companyID: user.attributes['custom:fm_company_id']
				}
			}
			const response = await api("post", "fmDataAPI", "/fmDataAPI", body)
			const scriptResult = JSON.parse(response.response.scriptResult)

			if(scriptResult?.messages?.code !== 0){
				throw scriptResult?.messages
			}

			const base64 = scriptResult.result.data
			const byteCharacters = atob(base64)
			const byteNumbers = new Array(byteCharacters.length)
			for(let i = 0; i < byteCharacters.length; i++) {
				byteNumbers[i] = byteCharacters.charCodeAt(i)
			}
			const byteArray = new Uint8Array(byteNumbers)
			const blob = new Blob([byteArray], {type: "application/pdf"})

			const url = URL.createObjectURL(blob)
			const link = document.createElement("a")
			link.href = url
			link.download = `DB Services Invoice #${props.invoice.invoiceNumber}.pdf`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(url)
		}
		catch(e) {
			console.error(e)
			alert("Unable to download invoice. Please try again.")
		}
		finally {
			downloadLoading.value = false
		}
	}

	function invoiceTotalWithoutProcessingFee(invoice: any) {
		
		const processingFee = invoice.services.filter((s: {description: string, total: number}) => {
			return s.description?.toLowerCase().includes('processing fee')
		})

		const amountDue = +invoice.total - (processingFee.length > 0 ? processingFee[0].total : 0)
		
		if (amountDue < 0) {
			return 0
		}

		return amountDue 
	}
</script>
